/**
 * @file homePath.ts
 * @brief Role → post-login home path for WEBPANEL.
 *
 * @details
 * Employees (`role: user`) land in the Boardroom app space (`/app`) so
 * no-phone / laptop users are not sent to `/forbidden`. Admins keep their
 * control-plane homes one click away.
 *
 * @author DES
 * @date 28-07-2026
 * @updated 13-10-2026 — DES: a superadmin lands on Home (`/admin/home`) like
 *   everyone else; the platform Dashboard stays at `/admin`.
 */

import type { AuthUser } from "@/shared/types/api";

export function isCompanyAdminRole(role: string | undefined): boolean {
  return role === "admin" || role === "company_admin";
}

/** Default landing path after login / bare `/`. */
export function homePathForUser(user: Pick<AuthUser, "role">): string {
  // Home first, as everyone else (DES 13-10-2026); Dashboard is /admin.
  if (user.role === "superadmin") return "/admin/home";
  // Home first, as on the phone; the Dashboard is one click away (30-09-2026).
  if (isCompanyAdminRole(user.role)) return "/company/home";
  // A user lands on Home, as on the phone (30-09-2026).
  return "/app";
}

/**
 * Deep-link `from` is only honoured when it sits inside the caller's
 * role space (prevents bouncing a superadmin into `/company`, etc.).
 */
export function allowedPathPrefixForUser(
  user: Pick<AuthUser, "role">,
): string {
  if (user.role === "superadmin") return "/admin";
  if (isCompanyAdminRole(user.role)) return "/company";
  return "/app";
}
